import React from 'react';
import {
    TextalignCenter,
    TextalignLeft,
    TextalignRight,
    DocumentText,
    SidebarTop,
    User,
} from '@/components/icons';
import { Checkbox } from '@/components/ui/checkbox';
import CollapsibleSection from '@/components/ui/collapsible-section';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { BRAZILIAN_STATES } from '@/constants';
import { useAuth } from '@/context/AuthContext';
import { masks } from '@/utils/masks';
import { formatCrpDisplay, validateCrp, validateFullName } from '../profile-fields';
import { summarizeHeader } from '../summarize';
import { InlineProfileField } from './InlineProfileField';
import type { HeaderCustomizations } from '@/components/preview/types';

interface HeaderSectionProps {
    open: boolean;
    onToggle: () => void;
    config: HeaderCustomizations;
    updateConfig: (updates: Partial<HeaderCustomizations>) => void;
    hasCrp: boolean;
}

/**
 * What shows at the top of every page and how it is aligned. Logo and letterhead
 * moved to `BrandAssetsSection` — they are brand, not header layout.
 */
export function HeaderSection({
    open,
    onToggle,
    config,
    updateConfig,
    hasCrp,
}: HeaderSectionProps) {
    const { user } = useAuth();

    return (
        <CollapsibleSection
            title="Cabeçalho"
            testId="customize-section-header"
            icon={SidebarTop}
            summary={summarizeHeader(config, { hasCrp })}
            open={open}
            onToggle={onToggle}
            contentClassName="space-y-2"
        >
            {/* Psychologist Name */}
            <div className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-surface">
                <Checkbox
                    id="show-name"
                    checked={config.show_psychologist_name !== false}
                    onCheckedChange={(checked) =>
                        updateConfig({
                            show_psychologist_name: !!checked,
                        })
                    }
                    className="mt-0.5"
                />
                <div className="flex-1">
                    <Label
                        htmlFor="show-name"
                        className="flex cursor-pointer items-center gap-2 text-sm font-medium"
                    >
                        <User className="size-4 text-muted-foreground" />
                        Nome do psicólogo
                    </Label>
                    <InlineProfileField
                        testId="customize-profile-name"
                        display={user?.full_name ?? ''}
                        emptyLabel="Adicionar nome"
                        initialDraft={{ full_name: user?.full_name ?? '' }}
                        validate={validateFullName}
                        successMessage="Nome atualizado"
                        renderFields={(draft, set) => (
                            <Input
                                autoFocus
                                value={draft.full_name ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    set({ full_name: e.target.value })
                                }
                                placeholder="ex: Ana Souza"
                                className="text-xs"
                            />
                        )}
                    />
                </div>
            </div>

            {/* CRP Number. `checked` follows `hasCrp` because clearing the CRP
                is reachable from this very row now — otherwise the box sits
                checked while disabled, claiming a CRP the laudo cannot print. */}
            <div className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-surface">
                <Checkbox
                    id="show-crp"
                    checked={hasCrp && config.show_crp_number !== false}
                    disabled={!hasCrp}
                    onCheckedChange={(checked) =>
                        updateConfig({
                            show_crp_number: !!checked,
                        })
                    }
                    className="mt-0.5"
                />
                <div className="flex-1">
                    <Label
                        htmlFor="show-crp"
                        className={`flex items-center gap-2 text-sm font-medium ${
                            !hasCrp ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
                        }`}
                    >
                        <DocumentText className="size-4 text-muted-foreground" />
                        Número do CRP
                    </Label>
                    <InlineProfileField
                        testId="customize-profile-crp"
                        display={formatCrpDisplay(user?.crp, user?.crp_state)}
                        emptyLabel="Adicionar CRP"
                        initialDraft={{
                            crp: user?.crp ?? '',
                            crp_state: user?.crp_state ?? '',
                        }}
                        validate={validateCrp}
                        successMessage="CRP atualizado"
                        renderFields={(draft, set) => (
                            <>
                                <Input
                                    autoFocus
                                    value={draft.crp ?? ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        set({ crp: masks.crp(e.target.value) })
                                    }
                                    placeholder="00/00000"
                                    maxLength={9}
                                    className="text-xs"
                                />
                                <Select
                                    value={draft.crp_state ?? ''}
                                    onValueChange={(value: string) => set({ crp_state: value })}
                                >
                                    <SelectTrigger className="w-20 shrink-0 text-xs">
                                        <SelectValue placeholder="UF" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {BRAZILIAN_STATES.map((uf) => (
                                            <SelectItem key={uf} value={uf}>
                                                {uf}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </>
                        )}
                    />
                </div>
            </div>

            {/* Description */}
            <div className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-surface">
                <Checkbox
                    id="show-description"
                    checked={config.description?.showing || false}
                    onCheckedChange={(checked) =>
                        updateConfig({
                            description: {
                                ...config.description,
                                showing: !!checked,
                            },
                        })
                    }
                    className="mt-0.5"
                />
                <div className="flex-1">
                    <Label
                        htmlFor="show-description"
                        className="flex cursor-pointer items-center gap-2 text-sm font-medium"
                    >
                        <DocumentText className="size-4 text-muted-foreground" />
                        Descrição
                    </Label>
                    {config.description?.showing && (
                        <Input
                            value={config.description?.value || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                updateConfig({
                                    description: {
                                        ...config.description,
                                        value: e.target.value,
                                    },
                                })
                            }
                            placeholder="ex: Neuropsicóloga especialista"
                            className="mt-2 text-xs"
                        />
                    )}
                </div>
            </div>

            {/* Alinhamento do cabecalho */}
            <div className="space-y-2 rounded-lg border border-border bg-card p-3">
                <Label className="text-sm font-medium">Alinhamento</Label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                    {(
                        [
                            {
                                value: 'left' as const,
                                label: 'Esquerda',
                                Icon: TextalignLeft,
                            },
                            {
                                value: 'center' as const,
                                label: 'Centro',
                                Icon: TextalignCenter,
                            },
                            {
                                value: 'right' as const,
                                label: 'Direita',
                                Icon: TextalignRight,
                            },
                        ] as const
                    ).map(({ value, label, Icon }) => (
                        <button
                            key={value}
                            onClick={() => updateConfig({ alignment: value })}
                            className={`flex flex-col items-center gap-2 rounded-lg border-2 p-3 transition-all ${
                                (config.alignment || 'left') === value
                                    ? 'border-[#00006B] bg-[#00006B] text-white'
                                    : 'border-border bg-card text-foreground hover:border-border-strong'
                            }`}
                        >
                            <Icon className="size-4" />
                            <span className="text-xs font-medium">{label}</span>
                        </button>
                    ))}
                </div>
            </div>
        </CollapsibleSection>
    );
}
